import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem as SelectMenuItem,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Lock as LockIcon,
  Add as AddIcon,
  Email as EmailIcon,
  FilterList as FilterIcon,
  PersonAdd as PersonAddIcon,
  UploadFile as UploadFileIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import axios from '../../api'; 
import { toast, ToastContainer } from 'react-toastify';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import InputAdornment from '@mui/material/InputAdornment';
import AdminLayout from '../AdminLayout';
import BulkUploadDialog from '../common/BulkUploadDialog';
import { useNavigate } from 'react-router-dom';
import { ROLES } from '../../utils/roles';

const USER_BULK_COLUMNS = [
  { key: 'name', label: 'name', required: true, note: 'Full name' },
  { key: 'email', label: 'email', required: true, note: 'Unique login email. Duplicate emails are skipped. Each user receives an activation email to set their own password.' },
  { key: 'empId', label: 'empId', note: 'Employee ID used to match assigned assets.' },
];

const USER_BULK_SAMPLE = {
  name: 'Jane Doe',
  email: 'jane.doe@example.com',
  empId: 'EMP-1001',
};

const PASSWORD_HINT = 'At least 8 characters with a letter and a number.';

function UserForm({ selectedUser, companyInfo, onCancel, onSuccess, open }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    empId: '',
    role: '',
    divisions: [],
    status: 'Active',
    organization: '',
  });
  const [orgOptions, setOrgOptions] = useState([]);
  const isSuper = ROLES.SUPER.includes(
    JSON.parse(localStorage.getItem('adminUser') || 'null')?.role
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const isEditing = !!selectedUser;

  const STATUS_OPTIONS = [
    { value: 'Active', label: 'Active' },
    { value: 'Inactive', label: 'Inactive' },
    { value: 'Suspended', label: 'Suspended' },
    { value: 'Left', label: 'Left' },
    { value: 'Terminated', label: 'Terminated' },
  ];

  const ROLE_OPTIONS = [
    { value: 'Admin', label: 'Admin' },
    { value: 'Manager', label: 'Manager' },
    { value: 'Auditor', label: 'Auditor (read-only)' },
    { value: 'Employee', label: 'Employee' },
  ]

  useEffect(() => {
    if (selectedUser) {
      setFormData({
        name: selectedUser.name || '',
        email: selectedUser.email || '',
        empId: selectedUser.empId || '',
        role: selectedUser.role || '',
        status: selectedUser.status || 'Active',
        organization: selectedUser.organization || '',
      });
    } else {
      setFormData({
        name: '',
        email: '',
        empId: '',
        reporter: 'Self',
        role: '',
        divisions: [],
        status: 'Active',
        organization: '',
      });
    }
    setValidationErrors({});
    setError('');
  }, [selectedUser, open]);

  useEffect(() => {
    if (open && isSuper) {
      axios
        .get('/organizations')
        .then((res) => setOrgOptions(res.data.result || []))
        .catch(() => setOrgOptions([]));
    }
  }, [open, isSuper]);

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }

    if (!formData.role) {
      errors.role = 'Role is required';
    }

    if (!formData.status) {
      errors.status = 'Status is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const removeEmptyKeys = (obj) => {
    const cleaned = {};
    Object.keys(obj).forEach(key => {
      if (obj[key] !== '' && obj[key] !== null && obj[key] !== undefined) {
        if (Array.isArray(obj[key]) && obj[key].length > 0) {
          cleaned[key] = obj[key];
        } else if (!Array.isArray(obj[key])) {
          cleaned[key] = obj[key];
        }
      }
    });
    return cleaned;
  };

const handleSubmit = async (invite = false) => {
  if (!validateForm()) return;

  setLoading(true);
  setError('');

  try {
    
    removeEmptyKeys(formData);

    const { name, email, empId, role, status, organization, _id } = formData;
    const payload = {
      name,
      email,
      empId,
      role,
      status: invite ? 'Active' : status,
      invite,
      _id,
      ...(organization ? { organization } : {}),
    };

    let response;
    console.log("isEditing && selectedUser", isEditing && selectedUser?.user);
    if (isEditing && selectedUser?.user) {
      response = await axios.put(`/users/update-user-mapping/${selectedUser?.user}`, payload);
    } else {
      response = await axios.post('/users/add-user-mapping', payload);
    }

    if (response.data?.result) {
      onSuccess?.(isEditing ? 'updated' : 'created', response.data.result);
      toast.success(
        response.data.message ||
          `User ${isEditing ? 'updated' : 'created'} successfully!`
      );
    } else {
      setError('Operation failed. Please try again.');
    }

  } catch (err) {
    console.error('Form submission error:', err);
    setError(err.response?.data?.message || 'An error occurred. Please try again.');
  } finally {
    setLoading(false);
  }
};

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Box sx={{ 
            bgcolor: isEditing ? '#ed6c02' : '#1976d2', 
            borderRadius: '50%', 
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {isEditing ? <EditIcon sx={{ color: 'white', fontSize: 20 }} /> : <PersonAddIcon sx={{ color: 'white', fontSize: 20 }} />}
          </Box>
          <Typography variant="h6">
            {isEditing ? 'Edit User' : 'Add New User'}
          </Typography>
        </Box>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          {isEditing
            ? 'Update user information below'
            : 'Fill in the details to create a new user. Use Add & Invite to email an account activation / set-password link.'}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Employee Name"
              variant="outlined"
              fullWidth
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              disabled={loading}
              required
              error={!!validationErrors.name}
              helperText={validationErrors.name}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Official Email ID"
              type="email"
              variant="outlined"
              fullWidth
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              disabled={loading}
              required
              error={!!validationErrors.email}
              helperText={
                validationErrors.email ||
                (isEditing
                  ? "Changing email updates their login and linked device assignments."
                  : undefined)
              }
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Employee ID"
              variant="outlined"
              fullWidth
              value={formData.empId}
              onChange={(e) => handleInputChange('empId', e.target.value)}
              disabled={loading}
              error={!!validationErrors.empId}
              helperText={validationErrors.empId}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!validationErrors.role}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.role}
                  onChange={(e) => handleInputChange('role', e.target.value)}
                  label="Role"
                  disabled={loading}
                >
                  {ROLE_OPTIONS.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label || role.value}
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.role && (
                  <FormHelperText>{validationErrors.role}</FormHelperText>
                )}
              </FormControl>
            </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!validationErrors.status}>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                label="Status"
                disabled={loading}
              >
                {STATUS_OPTIONS.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
              {validationErrors.status && (
                <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                  {validationErrors.status}
                </Typography>
              )}
            </FormControl>
          </Grid>
          {isSuper && (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Organization</InputLabel>
                <Select
                  value={formData.organization || ''}
                  onChange={(e) => handleInputChange('organization', e.target.value)}
                  label="Organization"
                  disabled={loading}
                >
                  <MenuItem value="">Default</MenuItem>
                  {orgOptions.map((o) => (
                    <MenuItem key={o._id} value={o._id}>
                      {o.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>
        
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleSubmit(false)}
          disabled={loading}
          sx={{ minWidth: 120 }}
        >
          {loading ? 'Saving...' : (isEditing ? 'Update' : 'Add User')}
        </Button>

        {!isEditing && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => handleSubmit(true)}
              disabled={loading}
              sx={{ minWidth: 140 }}
              title="Sends account activation / set-password email (not the undertaking form)"
            >
              {loading ? 'Saving...' : 'Add & Invite'}
            </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function User({ t = (key) => key, onLogout }) {
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openAddUser, setOpenAddUser] = useState(false);
  const [openResetPassword, setOpenResetPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmpassword, setShowConfirmPassword] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [openBulk, setOpenBulk] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuUser, setMenuUser] = useState(null);
  const navigate = useNavigate();
  
  const [companyInfo] = useState({
    configurations: {
      division: 'Yes' 
    }
  });
  
  const [filters, setFilters] = useState({
    name: '',
    email: '',
    role: '',
    status: '',
  });

  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10
  });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.get('/users/getUsersByCompany', {
        params: {
          name: filters.name || undefined,
          email: filters.email || undefined,
          role: filters.role || undefined,
          status: filters.status || undefined,
          page: pagination.page,
          perPage: pagination.limit,
        } 
      });
      
      if (response.data?.result) {
        setUsers(response?.data?.result);
        localStorage.setItem('user', JSON.stringify(response.data.result));
        setPagination((prev) => ({
          ...prev,
          total: response.data.pageData?.total || 0,
          // Keep the page the user requested; only sync if API returns a different page
          page: response.data.pageData?.page || prev.page,
          limit: response.data.pageData?.limit || prev.limit,
        }));
      }
    } catch (err) {
      console.error('Fetch users error:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleMenuClick = (event, user) => {
    setAnchorEl(event.currentTarget);
    setMenuUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuUser(null);
  };

  const handleEditUser = () => {
    setSelectedUser(menuUser);
    setOpenAddUser(true);
    handleMenuClose();
  };

  const handleResetPassword = () => {
    setSelectedUser(menuUser);
    setOpenResetPassword(true);
    handleMenuClose();
  };

  const handleResendActivation = async () => {
    const target = menuUser;
    handleMenuClose();
    if (!target?.user) return;
    setLoading(true);
    setError('');
    try {
      const response = await axios.post('/users/sendInvitation', {
        users: [target.user],
      });
      if (response.data.success) {
        setSuccess(
          `Activation link sent to ${target.email || 'user'}.`
        );
        // Keep existing status — resend does not reactivate leavers.
      } else {
        setError(
          response.data.errors?.[0] ||
            response.data.message ||
            'Failed to send activation link.'
        );
      }
    } catch (err) {
      console.error('Resend activation error:', err);
      setError(
        err.response?.data?.message ||
          'Failed to send activation link. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    const target = menuUser;
    handleMenuClose();
    if (!target) return;
    if (!window.confirm(`Delete user ${target.name || target.email}? This cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`/users/${target.user}`);
      toast.success('User deleted');
      setUsers((prev) => prev.filter((u) => u.user !== target.user));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete user');
    }
  };

  const handleAddUser = () => {
    setSelectedUser(null); 
    setOpenAddUser(true);
  };

  const handleCloseAddUser = () => {
    setOpenAddUser(false);
    setSelectedUser(null);
  };

  const handleUserFormSuccess = (action, userData) => {
    console.log("action", action);
    setSuccess(`User ${action} successfully!`);

    if (action === 'updated') {
      setUsers((prev) =>
        prev.map((u) => (u.user === userData.user ? { ...u, ...userData } : u))
      );
    } 
    else if (action === 'created') {
      setUsers((prev) => [userData, ...prev]);
    }
    
    setPagination((prev) => ({ ...prev, page: 1 }));
    handleCloseAddUser();
  };

  const handleSelectUser = (user, checked) => {
    if (checked) {
      setSelectedUsers([...selectedUsers, user]);
    } else {
      setSelectedUsers(selectedUsers.filter(u => u.user !== user.user));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedUsers(users.filter(u => u.userType !== 'Admin'));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSendInvitations = async () => {
    if (selectedUsers.length === 0) return;
    
    setLoading(true);
    setError('');
    
    try {
      const userIds = selectedUsers.map(user => user.user);
      const response = await axios.post('/users/sendInvitation', {
        users: userIds
      });
      
      if (response.data.success) {
        setSuccess('Invitations sent successfully!');
        setSelectedUsers([]);
      }
    } catch (err) {
      console.error('Send invitation error:', err);
      setError('Failed to send invitations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendInstructions = async () => {
    if (selectedUsers.length === 0) return;
    setLoading(true);
    setError('');
    try {
      const userIds = selectedUsers.map((user) => user.user);
      const response = await axios.post('/users/send-instructions', {
        users: userIds,
      });
      setSuccess(response.data.message || 'Instructions sent successfully!');
      setSelectedUsers([]);
    } catch (err) {
      console.error('Send instructions error:', err);
      setError(
        err.response?.data?.message ||
          'Failed to send instructions. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Filters change → jump back to first page (drives refetch via pagination.page)
    setPagination((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      case 'suspended': return 'warning';
      case 'left': return 'warning';
      case 'terminated': return 'error';
      default: return 'default';
    }
  };

  const fetchResetPassword = async () => {
    setError('');
    if (!password || !confirmPassword) {
      toast.error('Please fill all password fields');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/users/resetPassword', {
        user: selectedUser.user,
        password,
        confirmPassword
      });
      if (response.data.success) {
        toast.success('Password Reset Successfully!');
        setOpenResetPassword(false);
        setPassword('');
        setConfirmPassword('');
      } else {
        setError(response.data.message || 'Failed to reset password.');
      }
    } catch (err) {
      console.error('Reset password error:', err);
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  const handleLogout = () => {
    if (onLogout) return onLogout();
    navigate('/login');
  };

  return (
    <AdminLayout title="Users" onLogout={handleLogout}>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h4" component="h1" gutterBottom>
              {t('Users Overview')}
            </Typography>
          </Grid>
          <Grid item>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => setOpenFilter(true)}
              >
                {t('Filter')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => setOpenBulk(true)}
              >
                {t('Bulk Upload')}
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddUser}
              >
                {t('Add User')}
              </Button>
            </Box>
          </Grid>
        </Grid>

        {selectedUsers.length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<EmailIcon />}
              onClick={handleSendInvitations}
              disabled={loading}
              title="Account activation / set-password — not the undertaking form"
            >
              {t('Send activation')} ({selectedUsers.length})
            </Button>
            <Typography variant="caption" color="text.secondary">
              Activation emails create login access. For the undertaking form, use Dashboard → Send invite.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<EmailIcon />}
              onClick={handleSendInstructions}
              disabled={loading}
            >
              Send instructions ({selectedUsers.length})
            </Button>
          </Box>
        )}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Paper elevation={2}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedUsers.length > 0 && selectedUsers.length < users.filter(u => u.userType !== 'Admin').length}
                    checked={selectedUsers.length > 0 && selectedUsers.length === users.filter(u => u.userType !== 'Admin').length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </TableCell>
                <TableCell>{t('Name')}</TableCell>
                <TableCell>{t('Email ID')}</TableCell>
                <TableCell>{t('Emp ID')}</TableCell>
                <TableCell>{t('Reporter')}</TableCell>
                <TableCell>{t('Role')}</TableCell>
                <TableCell>{t('Type')}</TableCell>
                <TableCell>{t('Status')}</TableCell>
                <TableCell>{t('Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography>Loading...</Typography>
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography color="textSecondary">No users found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.user} hover>
                    <TableCell padding="checkbox">
                      {user.userType !== 'Admin' && (
                        <Checkbox
                          checked={selectedUsers.some(u => u.user === user.user)}
                          onChange={(e) => handleSelectUser(user, e.target.checked)}
                        />
                      )}
                    </TableCell>
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.empId || '-'}</TableCell>
                    <TableCell>{user.reporterName || 'Self'}</TableCell>
                    <TableCell>{user.roleName}</TableCell>
                    <TableCell>{user.userType}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.status}
                        color={getStatusColor(user.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {user.userType !== 'Admin' && (
                        <IconButton
                          onClick={(e) => handleMenuClick(e, user)}
                          size="small"
                        >
                          <MoreVertIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {pagination.total > 0 && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <Button
              disabled={pagination.page === 1}
              onClick={() => handlePageChange(pagination.page - 1)}
            >
              Previous
            </Button>
            <Typography sx={{ mx: 2, alignSelf: 'center' }}>
              Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit)}
            </Typography>
            <Button
              disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
              onClick={() => handlePageChange(pagination.page + 1)}
            >
              Next
            </Button>
          </Box>
        )}
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditUser}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          {t('Edit')}
        </MenuItem>
        <MenuItem onClick={handleResetPassword}>
          <LockIcon fontSize="small" sx={{ mr: 1 }} />
          {t('Reset Password')}
        </MenuItem>
        <MenuItem onClick={handleResendActivation}>
          <EmailIcon fontSize="small" sx={{ mr: 1 }} />
          Resend activation (set-password)
        </MenuItem>
        <MenuItem onClick={handleDeleteUser} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          {t('Delete')}
        </MenuItem>
      </Menu>

      <UserForm
        open={openAddUser}
        selectedUser={selectedUser}
        companyInfo={companyInfo}
        onCancel={handleCloseAddUser}
        onSuccess={handleUserFormSuccess}
      />

      <Dialog
        open={openResetPassword}
        onClose={() => setOpenResetPassword(false)}
        maxWidth="sm"
      >
        <DialogTitle>{t('Reset Password')}</DialogTitle>
        <DialogContent>
          <Typography variant="h6" color="textSecondary" sx={{ mt: 1 }}>
            {t('Please enter a new password and confirm it.')}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {PASSWORD_HINT}
          </Typography>
          <TextField
            fullWidth
            label={t('New Password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            type={showPassword ? 'text' : 'password'}
            variant="outlined"
            margin="dense"
            helperText={PASSWORD_HINT}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <TextField
            fullWidth
            label={t('Confirm Password')}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            type={showConfirmpassword ? 'text' : 'password'}
            variant="outlined"
            margin="dense"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    edge="end"
                  >
                    {showConfirmpassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenResetPassword(false)}>
            {t('Cancel')}
          </Button>
          <Button variant="contained" onClick={fetchResetPassword} color="primary">
            {t('Reset Password')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openFilter}
        onClose={() => setOpenFilter(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('Filter Users')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('Name')}
                value={filters.name}
                onChange={(e) => handleFilterChange('name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('Email')}
                value={filters.email}
                onChange={(e) => handleFilterChange('email', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label={t('Status')}
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <SelectMenuItem value="">{t('All')}</SelectMenuItem>
                <SelectMenuItem value="Active">{t('Active')}</SelectMenuItem>
                <SelectMenuItem value="Inactive">{t('Inactive')}</SelectMenuItem>
                <SelectMenuItem value="Suspended">{t('Suspended')}</SelectMenuItem>
                <SelectMenuItem value="Left">{t('Left')}</SelectMenuItem>
                <SelectMenuItem value="Terminated">{t('Terminated')}</SelectMenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('Role')}
                value={filters.role}
                onChange={(e) => handleFilterChange('role', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenFilter(false)}>
            {t('Cancel')}
          </Button>
          <Button 
            variant="contained" 
            onClick={() => {
              setOpenFilter(false);
              fetchUsers();
            }}
          >
            {t('Apply Filters')}
          </Button>
        </DialogActions>
      </Dialog>

      <BulkUploadDialog
        open={openBulk}
        onClose={() => setOpenBulk(false)}
        title="Bulk Import Users"
        endpoint="/users/bulk"
        columns={USER_BULK_COLUMNS}
        sample={USER_BULK_SAMPLE}
        templateName="users-template"
        onImported={() => fetchUsers()}
      />

        <ToastContainer position="top-center" autoClose={2000} />
    </AdminLayout>
  );
}

export default User;